(function () {
  const PROFILE_STORAGE_KEY = 'inshape_quiz_profile_v1';
  const PROFILE_CLEAR_PENDING_KEY = 'inshape_quiz_profile_clear_pending_v1';
  const PROFILE_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;
  const TARGET_FUNNEL = 'buyer_profile_quiz_v1';
  const PRODUCT_FORM_SELECTOR = 'form[data-type="add-to-cart-form"], form[action*="/cart/add"]';
  const PRODUCT_PROPERTY_MARKER = 'data-inshape-quiz-attribution';
  const PRODUCT_BIND_MARKER = 'data-inshape-quiz-attribution-bound';
  const PRODUCT_SIGNATURE_MARKER = 'data-inshape-quiz-attribution-signature';
  const CART_ADD_PATTERN = /\/cart\/add(?:\.js)?(?:$|[/?])/i;

  function safeParse(value) {
    if (!value) {
      return null;
    }

    try {
      return JSON.parse(value);
    } catch (error) {
      return null;
    }
  }

  function readProfile() {
    try {
      const profile = safeParse(window.localStorage.getItem(PROFILE_STORAGE_KEY));

      if (!profile || typeof profile !== 'object') {
        return null;
      }

      const completedAt = Date.parse(profile.completedAt || '');

      if (!Number.isFinite(completedAt) || Date.now() - completedAt > PROFILE_MAX_AGE_MS) {
        clearStoredProfile();
        return null;
      }

      return profile;
    } catch (error) {
      return null;
    }
  }

  function normalizePath(path) {
    if (!path) {
      return '';
    }

    return String(path).split('?')[0].replace(/\/+$/, '') || '/';
  }

  function normalizeUrlPath(url) {
    if (!url) {
      return '';
    }

    try {
      return normalizePath(new URL(String(url), window.location.origin).pathname);
    } catch (error) {
      return normalizePath(url);
    }
  }

  function shouldApplyProfile(profile, path) {
    if (!profile || profile.quizVersion !== TARGET_FUNNEL) {
      return false;
    }

    const currentPath = normalizePath(path || window.location.pathname);
    const profilePath = normalizePath(profile.resultPathBase);

    return Boolean(profilePath) && currentPath === profilePath;
  }

  function getActiveProfile(path) {
    const profile = readProfile();

    if (!shouldApplyProfile(profile, path)) {
      return null;
    }

    return profile;
  }

  function isCartAddUrl(url) {
    return CART_ADD_PATTERN.test(normalizeUrlPath(url));
  }

  function removeInjectedFields(form) {
    form.querySelectorAll('[' + PRODUCT_PROPERTY_MARKER + '="true"]').forEach((node) => node.remove());
  }

  function clearStoredProfile() {
    try {
      window.localStorage.removeItem(PROFILE_STORAGE_KEY);
    } catch (error) {
    }

    clearPendingProfileCleanup();
  }

  function markPendingProfileCleanup() {
    try {
      window.sessionStorage.setItem(PROFILE_CLEAR_PENDING_KEY, '1');
    } catch (error) {
    }
  }

  function hasPendingProfileCleanup() {
    try {
      return window.sessionStorage.getItem(PROFILE_CLEAR_PENDING_KEY) === '1';
    } catch (error) {
      return false;
    }
  }

  function clearPendingProfileCleanup() {
    try {
      window.sessionStorage.removeItem(PROFILE_CLEAR_PENDING_KEY);
    } catch (error) {
    }
  }

  function serializePropertyValue(value) {
    return typeof value === 'string' ? value : JSON.stringify(value);
  }

  function appendHiddenProperty(form, label, value) {
    if (value == null || value === '') {
      return;
    }

    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = 'properties[' + label + ']';
    input.value = serializePropertyValue(value);
    input.setAttribute(PRODUCT_PROPERTY_MARKER, 'true');
    form.appendChild(input);
  }

  function buildOrderPayload(profile) {
    return {
      funnel: profile.quizVersion || TARGET_FUNNEL,
      sessionId: profile.sessionId || '',
      lane: profile.lane || '',
      issue: profile.issue || '',
      size: profile.size || '',
      completedAt: profile.completedAt || '',
      answers: profile.answerLabels || {},
      resultPathBase: profile.resultPathBase || '',
      sourcePath: profile.sourcePath || '',
      landingPath: profile.landingPath || '',
      attribution: profile.attribution || {}
    };
  }

  function buildPropertyEntries(profile) {
    const payload = buildOrderPayload(profile);

    return [
      ['_Quiz Funnel', payload.funnel],
      ['_Quiz Session ID', payload.sessionId],
      ['_Quiz Lane', payload.lane],
      ['_Quiz Issue', payload.issue],
      ['_Quiz Size', payload.size],
      ['_Quiz Completed At', payload.completedAt],
      ['_Quiz Answers JSON', payload.answers],
      ['_Quiz Attribution JSON', payload.attribution]
    ].filter((entry) => entry[1] != null && entry[1] !== '');
  }

  function buildPropertySignature(profile) {
    return buildPropertyEntries(profile)
      .map((entry) => entry[0] + ':' + serializePropertyValue(entry[1]))
      .join('|');
  }

  function findItemIndexes(keys) {
    const indexes = new Set();

    keys.forEach((key) => {
      const match = String(key).match(/^items\[(\d+)\]\[/);

      if (match) {
        indexes.add(match[1]);
      }
    });

    return Array.from(indexes);
  }

  function appendEntriesToFormData(formData, entries) {
    const itemIndexes = findItemIndexes(Array.from(formData.keys()));

    if (itemIndexes.length) {
      itemIndexes.forEach((index) => {
        entries.forEach((entry) => {
          const key = 'items[' + index + '][properties][' + entry[0] + ']';

          if (!formData.has(key)) {
            formData.append(key, serializePropertyValue(entry[1]));
          }
        });
      });

      return formData;
    }

    entries.forEach((entry) => {
      const key = 'properties[' + entry[0] + ']';

      if (!formData.has(key)) {
        formData.append(key, serializePropertyValue(entry[1]));
      }
    });

    return formData;
  }

  function appendEntriesToSearchParams(params, entries) {
    const itemIndexes = findItemIndexes(Array.from(params.keys()));

    if (itemIndexes.length) {
      itemIndexes.forEach((index) => {
        entries.forEach((entry) => {
          const key = 'items[' + index + '][properties][' + entry[0] + ']';

          if (!params.has(key)) {
            params.append(key, serializePropertyValue(entry[1]));
          }
        });
      });

      return params;
    }

    entries.forEach((entry) => {
      const key = 'properties[' + entry[0] + ']';

      if (!params.has(key)) {
        params.append(key, serializePropertyValue(entry[1]));
      }
    });

    return params;
  }

  function appendEntriesToPropertiesObject(target, entries) {
    if (!target || typeof target !== 'object') {
      return;
    }

    target.properties = target.properties && typeof target.properties === 'object' && !Array.isArray(target.properties)
      ? target.properties
      : {};

    entries.forEach((entry) => {
      if (!(entry[0] in target.properties)) {
        target.properties[entry[0]] = serializePropertyValue(entry[1]);
      }
    });
  }

  function appendEntriesToAttributesArray(target, entries) {
    if (!target || typeof target !== 'object') {
      return;
    }

    target.attributes = Array.isArray(target.attributes) ? target.attributes : [];

    entries.forEach((entry) => {
      const exists = target.attributes.some((attribute) => attribute && attribute.key === entry[0]);

      if (!exists) {
        target.attributes.push({
          key: entry[0],
          value: serializePropertyValue(entry[1])
        });
      }
    });
  }

  function augmentJsonPayload(payload, entries) {
    if (!payload || typeof payload !== 'object') {
      return payload;
    }

    const query = typeof payload.query === 'string' ? payload.query : '';
    const isGraphqlCartMutation = /cartCreate|cartLinesAdd/i.test(query);

    if (isGraphqlCartMutation) {
      const lineSets = [];

      if (payload.variables && payload.variables.input && Array.isArray(payload.variables.input.lines)) {
        lineSets.push(payload.variables.input.lines);
      }

      if (payload.variables && Array.isArray(payload.variables.lines)) {
        lineSets.push(payload.variables.lines);
      }

      if (payload.input && Array.isArray(payload.input.lines)) {
        lineSets.push(payload.input.lines);
      }

      lineSets.forEach((lines) => {
        lines.forEach((line) => {
          appendEntriesToAttributesArray(line, entries);
        });
      });

      return payload;
    }

    if (Array.isArray(payload.items)) {
      payload.items.forEach((line) => {
        appendEntriesToPropertiesObject(line, entries);
      });
      return payload;
    }

    if (Array.isArray(payload.lines)) {
      payload.lines.forEach((line) => {
        appendEntriesToPropertiesObject(line, entries);
      });
      return payload;
    }

    appendEntriesToPropertiesObject(payload, entries);
    return payload;
  }

  function isGraphqlCartMutationPayload(body, contentType) {
    const lowerContentType = String(contentType || '').toLowerCase();
    let payload = null;

    if (typeof body === 'string') {
      if (lowerContentType.indexOf('application/json') === -1) {
        return false;
      }

      payload = safeParse(body);
    } else if (body && typeof body === 'object') {
      payload = body;
    }

    return Boolean(payload && typeof payload.query === 'string' && /cartCreate|cartLinesAdd/i.test(payload.query));
  }

  function augmentBody(body, contentType, profile) {
    if (!profile || body == null) {
      return body;
    }

    const entries = buildPropertyEntries(profile);

    if (!entries.length) {
      return body;
    }

    if (body instanceof FormData) {
      return appendEntriesToFormData(body, entries);
    }

    if (body instanceof URLSearchParams) {
      return appendEntriesToSearchParams(body, entries);
    }

    if (typeof body === 'string') {
      if (String(contentType || '').toLowerCase().indexOf('application/json') !== -1) {
        const parsed = safeParse(body);

        if (!parsed) {
          return body;
        }

        return JSON.stringify(augmentJsonPayload(parsed, entries));
      }

      const params = new URLSearchParams(body);
      appendEntriesToSearchParams(params, entries);
      return params.toString();
    }

    if (typeof body === 'object') {
      return augmentJsonPayload(body, entries);
    }

    return body;
  }

  function bindPendingCleanup(form) {
    if (form.getAttribute(PRODUCT_BIND_MARKER) === 'true') {
      return;
    }

    form.setAttribute(PRODUCT_BIND_MARKER, 'true');
    form.addEventListener('submit', () => {
      markPendingProfileCleanup();
    });
  }

  function applyProfileToForm(form, profile) {
    const signature = buildPropertySignature(profile);

    if (form.getAttribute(PRODUCT_SIGNATURE_MARKER) === signature) {
      bindPendingCleanup(form);
      return;
    }

    removeInjectedFields(form);

    buildPropertyEntries(profile).forEach((entry) => {
      appendHiddenProperty(form, entry[0], entry[1]);
    });

    form.setAttribute(PRODUCT_SIGNATURE_MARKER, signature);
    bindPendingCleanup(form);
  }

  function applyProfile() {
    const profile = getActiveProfile(window.location.pathname);

    if (!profile) {
      return;
    }

    document.querySelectorAll(PRODUCT_FORM_SELECTOR).forEach((form) => {
      applyProfileToForm(form, profile);
    });
  }

  function resolveHeaders(headers) {
    try {
      return new Headers(headers || undefined);
    } catch (error) {
      return new Headers();
    }
  }

  function installFetchInterceptor() {
    if (typeof window.fetch !== 'function' || window.__inshapeQuizFetchPatched) {
      return;
    }

    const originalFetch = window.fetch.bind(window);

    window.fetch = function (input, init) {
      const url = typeof input === 'string' ? input : (input && input.url) || '';
      const method = String((init && init.method) || (input && input.method) || 'GET').toUpperCase();
      const headers = resolveHeaders((init && init.headers) || (input && input.headers));
      const contentType = headers.get('Content-Type') || headers.get('content-type') || '';
      const isTrackedRequest = method !== 'GET' && (
        isCartAddUrl(url) ||
        isGraphqlCartMutationPayload(init && init.body, contentType)
      );
      const profile = isTrackedRequest
        ? getActiveProfile(window.location.pathname)
        : null;
      let requestInit = init;

      if (profile && init && Object.prototype.hasOwnProperty.call(init, 'body')) {
        requestInit = Object.assign({}, init, {
          body: augmentBody(init.body, contentType, profile)
        });
      }

      const request = originalFetch(input, requestInit);

      if (!profile) {
        return request;
      }

      return request.then((response) => {
        if (response && response.ok) {
          clearStoredProfile();
        }

        return response;
      });
    };

    window.__inshapeQuizFetchPatched = true;
  }

  function installXhrInterceptor() {
    if (typeof window.XMLHttpRequest === 'undefined' || window.__inshapeQuizXhrPatched) {
      return;
    }

    const originalOpen = window.XMLHttpRequest.prototype.open;
    const originalSend = window.XMLHttpRequest.prototype.send;
    const originalSetRequestHeader = window.XMLHttpRequest.prototype.setRequestHeader;

    window.XMLHttpRequest.prototype.open = function (method, url) {
      this.__inshapeQuizMethod = method;
      this.__inshapeQuizUrl = url;
      this.__inshapeQuizHeaders = {};
      return originalOpen.apply(this, arguments);
    };

    window.XMLHttpRequest.prototype.setRequestHeader = function (key, value) {
      this.__inshapeQuizHeaders = this.__inshapeQuizHeaders || {};
      this.__inshapeQuizHeaders[String(key).toLowerCase()] = value;
      return originalSetRequestHeader.apply(this, arguments);
    };

    window.XMLHttpRequest.prototype.send = function (body) {
      const method = String(this.__inshapeQuizMethod || 'GET').toUpperCase();
      const url = this.__inshapeQuizUrl || '';
      const contentType = this.__inshapeQuizHeaders && this.__inshapeQuizHeaders['content-type']
        ? this.__inshapeQuizHeaders['content-type']
        : '';
      const profile = method !== 'GET' && isCartAddUrl(url)
        ? getActiveProfile(window.location.pathname)
        : null;
      const nextBody = profile ? augmentBody(body, contentType, profile) : body;

      if (profile) {
        this.addEventListener('load', function () {
          if (this.status >= 200 && this.status < 400) {
            clearStoredProfile();
          }
        }, { once: true });
      }

      return originalSend.call(this, nextBody);
    };

    window.__inshapeQuizXhrPatched = true;
  }

  function init() {
    let applyScheduled = false;
    let applyRunning = false;

    function runApplyProfile() {
      if (applyRunning) {
        return;
      }

      applyRunning = true;

      try {
        applyProfile();
      } finally {
        applyRunning = false;
      }
    }

    function scheduleApplyProfile() {
      if (applyScheduled) {
        return;
      }

      applyScheduled = true;
      window.requestAnimationFrame(() => {
        applyScheduled = false;
        runApplyProfile();
      });
    }

    if (hasPendingProfileCleanup()) {
      clearStoredProfile();
    }

    installFetchInterceptor();
    installXhrInterceptor();
    runApplyProfile();

    const observer = new MutationObserver(() => {
      scheduleApplyProfile();
    });

    observer.observe(document.documentElement, {
      childList: true,
      subtree: true
    });

    document.addEventListener('shopify:section:load', scheduleApplyProfile);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
